import { css } from '@lastbrand/web-ui-styled-system/css'

// Panda twin of BundleProductCard.module.scss — 1:1 visual translation of the `_newDesignV2` variants
// (the only variants still rendered; legacy fluid-rem values converted at a 10px root).

// `css.raw` (not `css`) so these compose via mergeLegacyResponsiveStyles at the call site:
// the add-on variants override the base by the SAME property keys in one merged css() call,
// which is deterministic — unlike cx(css(A), css(B)), where atomic-class order is not source
// order (see ai-guidelines/project/ds-migration.md).
export const bundleCardWrapperCss = css.raw({
  borderTop: '1px solid',
  borderColor: 'lightgray',
  paddingBlock: 'space-40',
  display: 'flex',
  flexDirection: 'column',
})

export const addOnBundleCss = css.raw({
  backgroundColor: 'paperwhite',
  // Longhand (not shorthand `padding`) so it overrides the base's `paddingBlock` on merge;
  // ProductTileBundle "upsell" variant spec: all-sides space-16 mobile / space-20 desktop
  // (STRFRNT-16944 Figma parity — was space-12 mobile).
  paddingBlock: { base: 'space-16', lg: 'space-20' },
  paddingInline: { base: 'space-16', lg: 'space-20' },
  borderTop: 'none',
  // No marginBottom: the AddOnList master's `list` owns the inter-tile rhythm (gap space-32 at
  // both breakpoints, Dev Mode 13036-367647/367792) via addOnBundleProductsListCss in
  // bundle-product-purchase-ds.styles — a per-tile margin would also leak below the last tile
  // into the CTA seam (STRFRNT-16944 spacing round).
})

export const bundleCardCss = css({
  display: 'flex',
})

export const imageContainerCss = css.raw({
  marginRight: 'space-20',
  // ProductTileBundle master, Default variant (Dev Mode 9359-113843): 96px-wide image column at
  // a FIXED 122.667px height (= 96 × 23/18; the master pins H, W is max-width). Pinned rather
  // than `aspectRatio` because the card row's default `align-items: stretch` would stretch an
  // auto height to the tallest sibling and defeat the ratio.
  width: '96px',
  height: '122.667px',
  flexShrink: 0,
})

export const addOnProductImageContainerCss = css.raw({
  // ProductTileBundle "upsell" variant spec (STRFRNT-16944): same 96 × 122.667px fixed image
  // column as the Default variant, at every breakpoint and expanded state. `height`/`width` are
  // overridden explicitly (not just `maxWidth`) because `mergeLegacyResponsiveStyles` is a
  // shallow Object.assign — the base's values would otherwise survive the merge.
  width: '96px',
  height: '122.667px',
  marginRight: 'space-20',
  flexShrink: 0,
})

export const productDetailsCss = css({
  width: 'full',
})

export const productOptionsContainerCss = css({
  // ProductTileBundle master: Body column gap is space-20 on mobile / space-24 on desktop
  // (STRFRNT-16907 — mWeb padding above the color option; was space-24 at all breakpoints).
  marginTop: { base: 'space-20', lg: 'space-24' },
  // STRFRNT-16948: the last option row's trailing margin (productOptionsSpacingCss) collapsed
  // out of this block and stacked under the card's space-40 bottom padding — 64px to the divider
  // vs the ProductBundleList spec's 40 (Figma 9359:116244: list gap space-40, measured live).
  // Zero it only when the options block ends the card (no size-chart/error rows after it), so the
  // options→size-chart gap is untouched.
  '&:last-child > :last-child': { marginBottom: 0 },
})

export const productOptionsSpacingCss = css({
  // ProductTileBundle master Body rhythm: space-20 mobile / space-24 desktop (STRFRNT-16907).
  marginTop: { base: 'space-20', lg: 'space-24' },
  marginBottom: { base: 'space-20', lg: 'space-24' },
  width: 'fit-content',
})

// Top offset comes from ProductPropertyDS's content wrapper; declaring it here too
// would duplicate the same value and race it in the Panda cascade.
export const siblingOptionsSelectorCss = css({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'space-10',
})

export const sizeChartWrapperCss = css({
  paddingTop: 'space-6',
  // The size-chart row carries no trailing space of its own, so when an error follows it the two
  // would sit flush. Re-add the 8px only for that adjacency — the error's distance from the option
  // rows is owned by their own rhythm (see errorMessageCss).
  '& + *': { marginTop: 'space-8' },
})

export const textOptionLabelNewDesignV2Css = css({
  backgroundColor: 'white',
})

export const errorMessageCss = css({
  textStyle: 'body-14', // legacy 13px/19px -> nearest token (14px/20px)
  fontWeight: 300, // legacy light weight; no light textStyle variant exists
  color: 'red-100',
  // STRFRNT-16946: no margin of its own. The last option row already closes with the card's Body
  // rhythm (productOptionsSpacingCss: space-20 mobile / space-24 desktop), which is exactly the
  // spec'd gap above the error; the old space-8 stacked on top of it for 28/32.
})
